import { Fragment, useState } from "react";
import Layout from "../../../components/layout";
import { useHistory, useParams } from "react-router-dom";
import { getDisciplineGroupRules, saveDisciplineGroupRules } from "../../../api/services/disciplineService";
import { useAsync } from "react-async";
import { getTreatments } from "../../../api/services/treatmentService";
import DisciplineGroupRule from "../../../api/models/disciplineGroupRule";
import Treatment from "../../../api/models/treatment";
import { TreatmentRuleTypeDisplay, TreatmentRuleTypeLookup } from "../../../common/constants";
import SubmitButton from "../../../components/submitButton";
import LayoutError from "../../../components/layoutError";

type Params = {
    id: string;
    disciplineGroupName: string;
};

const fetchDisciplineGroupRules = async ({ id }: any) => {
    return await getDisciplineGroupRules(id);
};

const fetchTreatments = async () => {
    return await getTreatments();
}

const EditDisciplineGroupRules = () => {
    const { id } = useParams<Params>();
    const history = useHistory();

    const [disciplineGroupRules, setDisciplineGroupRules] = useState<DisciplineGroupRule[]>([]);
    const [treatments, setTreatments] = useState<Treatment[]>([]);
    const [disciplineGroupName, setDisciplineGroupName] = useState<string>();

    const disciplineGroupRulesData = useAsync({
        promiseFn: fetchDisciplineGroupRules,
        id,
        onResolve: (r) => {
            setDisciplineGroupRules(r.data.disciplineGroupRules);
            setDisciplineGroupName(r.data.disciplineGroupName);
        },
    });

    const treatmentsData = useAsync({
        promiseFn: fetchTreatments,
        onResolve: (r) => {
            setTreatments(r.data.treatments);
        },
    });

    const saveFunction = async () => {
        return await saveDisciplineGroupRules(disciplineGroupRules);
    }

    const save = useAsync({
        deferFn: saveFunction,
        onResolve: () => {
            history.push('/admin/disciplineGroupRules', {
                message: `"${disciplineGroupName}" saved successfully!`,
            });
        },
        onReject: () => {
            window.scrollTo(0, 0);
        },
    });

    const habdleCheckboxClick = (treatmentId: number, treatmentType: TreatmentRuleTypeLookup, value: boolean) => {
        setDisciplineGroupRules(current => {
            let tempEntries: DisciplineGroupRule[] = [];
            current.forEach(item => {
                if (item.treatmentId === treatmentId) {
                    switch (treatmentType) {
                        case TreatmentRuleTypeLookup.Dropdown:
                            item.isDropdownAllowed = value;
                            break;
                        case TreatmentRuleTypeLookup.PlanRequired:
                            item.isPlanRequired = value;
                            break;
                        case TreatmentRuleTypeLookup.DatesRequired:
                            item.isDatesRequired = value;
                            break;
                        case TreatmentRuleTypeLookup.OtherDisciplines:
                            item.isOtherDisciplines = value;
                            break;
                    }
                }
                tempEntries.push(item);
            })

            return tempEntries;
        })
    }

    const handleSubmit = () => {
        save.run();
    };

    // Error handling
    if (disciplineGroupRulesData.error || treatmentsData.error) return <LayoutError />;

    return (
        <Layout isLoading={
            disciplineGroupRulesData.isPending
            || treatmentsData.isPending
        }>
            <Fragment>
                <h1 style={{ fontWeight: 'bold' }}>{disciplineGroupName}</h1>

                <br />

                {disciplineGroupRules && disciplineGroupRules.length > 0 &&
                    <div style={{ display: 'grid', gridTemplateColumns: `3fr repeat(${treatments.length}, 1fr)`, gridRowGap: '10px', backgroundColor: 'white', borderRadius: '8px' }}>
                        <p className="ml-2 "></p>
                        {treatments && treatments.map((t, index) => {
                            return (
                                <p style={{ fontWeight: 'bold' }}>{t.name}</p>
                            )
                        })}

                        <p className="ml-2" style={{ fontWeight: 'bold' }}>{TreatmentRuleTypeDisplay[TreatmentRuleTypeLookup.Dropdown]}</p>
                        {disciplineGroupRules && disciplineGroupRules.map((dr, index) => {
                            return (
                                <input
                                    id={`rule-option-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.Dropdown}`}
                                    name={`rule-option-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.Dropdown}`}
                                    key={`rule-option-checkbox-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.Dropdown}`}
                                    type="checkbox"
                                    checked={dr.isDropdownAllowed}
                                    onChange={(e) => habdleCheckboxClick(dr.treatmentId, TreatmentRuleTypeLookup.Dropdown, !dr.isDropdownAllowed)}
                                />
                            )
                        })}

                        <p className="ml-2" style={{ fontWeight: 'bold' }}>{TreatmentRuleTypeDisplay[TreatmentRuleTypeLookup.PlanRequired]}</p>
                        {disciplineGroupRules && disciplineGroupRules.map((dr, index) => {
                            return (
                                <input
                                    id={`rule-option-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.PlanRequired}`}
                                    name={`rule-option-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.PlanRequired}`}
                                    key={`rule-option-checkbox-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.PlanRequired}`}
                                    type="checkbox"
                                    checked={dr.isPlanRequired}
                                    onChange={(e) => habdleCheckboxClick(dr.treatmentId, TreatmentRuleTypeLookup.PlanRequired, !dr.isPlanRequired)}
                                />
                            )
                        })}

                        <p className="ml-2" style={{ fontWeight: 'bold' }}>{TreatmentRuleTypeDisplay[TreatmentRuleTypeLookup.DatesRequired]}</p>
                        {disciplineGroupRules && disciplineGroupRules.map((dr, index) => {
                            return (
                                <input
                                    id={`rule-option-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.DatesRequired}`}
                                    name={`rule-option-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.DatesRequired}`}
                                    key={`rule-option-checkbox-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.DatesRequired}`}
                                    type="checkbox"
                                    checked={dr.isDatesRequired}
                                    onChange={(e) => habdleCheckboxClick(dr.treatmentId, TreatmentRuleTypeLookup.DatesRequired, !dr.isDatesRequired)}
                                />
                            )
                        })}

                        <p className="ml-2 mb-2" style={{ fontWeight: 'bold' }}>{TreatmentRuleTypeDisplay[TreatmentRuleTypeLookup.OtherDisciplines]}</p>
                        {disciplineGroupRules && disciplineGroupRules.map((dr, index) => {
                            return (
                                <input
                                    id={`rule-option-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.OtherDisciplines}`}
                                    name={`rule-option-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.OtherDisciplines}`}
                                    key={`rule-option-checkbox-${dr.disciplineGroupId}_${dr.treatmentId}_${TreatmentRuleTypeLookup.OtherDisciplines}`}
                                    type="checkbox"
                                    checked={dr.isOtherDisciplines}
                                    onChange={(e) => habdleCheckboxClick(dr.treatmentId, TreatmentRuleTypeLookup.OtherDisciplines, !dr.isOtherDisciplines)}
                                />
                            )
                        })}
                    </div>
                }

                <hr className='py-4' />
                <div className='text-right'>
                    <SubmitButton
                        text='Save Changes'
                        disabled={false}
                        spinning={save.isPending}
                        onClick={handleSubmit}
                    />
                </div>
            </Fragment>
        </Layout>
    )
}

export default EditDisciplineGroupRules;